import * as React from "react";

import { DataDeclarationFactory, ValidationFunctionsFactory } from "../../../Forms/RawFormResources";
import { Deferred } from "../../../Helpers/Deferred";
import { NidusEntry, NidusName, NidusReferenceKey } from "../../../../../Common/NidusClient/INidusClient";
import { useContextualNidusClient } from "../../../../../Common/NidusClient/NidusClientContext";
import { ContextualActionsProvider } from "../../Internals/FormActionHandlers/ContextualActionsContext";
import { DraftFullId } from "../../../../../Common/CandyApiClient/Types/DraftFullId";
import { ensureNotNullOrUndefined, reject } from "../../../../../Common/TypingUtils";
import { AutocalculationConfigurator } from "../../../Forms/FormResources";

import { IUserPicklist } from "./UserPicklist/IUserPicklist";
import { useUserPicklistRegistry } from "./UserPicklist/UserPicklistRegistryContext";
import { SelectNidusEntryModal } from "./SelectNidusEntryModal/SelectNidusEntryModal";
import { CreateNidusEntryFormModal } from "./NidusDataEditors/CreateNidusEntryFormModal";
import { UpdateNidusEntryFormModal } from "./NidusDataEditors/UpdateNidusEntryFormModal";

export interface UserPicklistColumns {
    title: string;
    name?: string;
    names?: string[];
    width: string;
}

export interface UserPicklistDeclarationProps {
    name: NidusName;
    referenceKey: NidusReferenceKey;
    columns: UserPicklistColumns[];
    editor: React.ReactNode;
    editorValidations: ValidationFunctionsFactory;
    kcXmlValidationConfigurator: AutocalculationConfigurator;
    editorDataDeclaration: DataDeclarationFactory;
    pickerTitle?: React.ReactNode;
    pickerCreateLink?: React.ReactNode;
    createTitle?: React.ReactNode;
    editTitle?: React.ReactNode;
    emptyPickerMessage?: React.ReactNode;
    draftId: DraftFullId;
    keformsUrl: string;
}

export function UserPicklistDeclaration(props: UserPicklistDeclarationProps): JSX.Element {
    const userPicklistRegistry = useUserPicklistRegistry();
    const nidusClient = useContextualNidusClient(props.name, props.referenceKey);

    const createEntryFormResultDeferred = React.useRef<Deferred<undefined | NidusEntry>>();
    const [showCreateEntryForm, setShowCreateEntryForm] = React.useState(false);

    const createEntry = React.useCallback((): Promise<undefined | NidusEntry> => {
        createEntryFormResultDeferred.current = new Deferred<undefined | NidusEntry>();
        setShowCreateEntryForm(true);
        return createEntryFormResultDeferred.current.promise;
    }, []);

    const handleCreateEntryFormSave = async (item: NidusEntry) => {
        ensureNotNullOrUndefined(createEntryFormResultDeferred.current);
        setShowCreateEntryForm(false);
        createEntryFormResultDeferred.current.resolve(item);
    };

    const handleCreateEntryFormClose = async () => {
        ensureNotNullOrUndefined(createEntryFormResultDeferred.current);
        setShowCreateEntryForm(false);
        createEntryFormResultDeferred.current.resolve(undefined);
    };

    const handleCreateEntryFormError = () => {
        ensureNotNullOrUndefined(updateEntryFormResultDeferred.current);
        // Показать ошибку
        setShowCreateEntryForm(false);
        updateEntryFormResultDeferred.current.resolve(undefined);
    };

    const selectValueDeferred = React.useRef<Deferred<undefined | NidusEntry>>();
    const [getValueFromReferenceModal, setGetValueFromReferenceModal] = React.useState(false);

    const handleSelectEntryFromModal = async (nidusEntry: NidusEntry) => {
        ensureNotNullOrUndefined(selectValueDeferred.current);
        setGetValueFromReferenceModal(false);
        selectValueDeferred.current.resolve(nidusEntry);
    };

    const handleSelectEntryModalClose = async () => {
        ensureNotNullOrUndefined(selectValueDeferred.current);
        setGetValueFromReferenceModal(false);
        selectValueDeferred.current.resolve(undefined);
    };

    const handleCreateNewFromSelect = async () => {
        const createdEntry = await createEntry();
        return createdEntry;
    };

    const handleDeleteFromSelect = async (item: NidusEntry): Promise<void> => {
        await nidusClient.delete(item.id);
    };

    const handleRequestRestoreItem = async (item: NidusEntry): Promise<NidusEntry> =>
        await nidusClient.insert(item.data);

    const [showUpdateEntryForm, setShowUpdateEntryForm] = React.useState(false);
    const [entryForUpdate, setEntryForUpdate] = React.useState<undefined | NidusEntry>();
    const updateEntryFormResultDeferred = React.useRef<Deferred<undefined | NidusEntry>>();

    const handleRequestUpdateEntry = async (item: NidusEntry): Promise<NidusEntry> => {
        setEntryForUpdate(item);
        setShowUpdateEntryForm(true);
        updateEntryFormResultDeferred.current = new Deferred<undefined | NidusEntry>();
        return (await updateEntryFormResultDeferred.current.promise) ?? item;
    };

    const handleUpdateEntryFormClose = () => {
        ensureNotNullOrUndefined(updateEntryFormResultDeferred.current);
        setShowUpdateEntryForm(false);
        setEntryForUpdate(undefined);
        updateEntryFormResultDeferred.current.resolve(undefined);
    };

    const handleUpdateEntryFormSave = (updatedItem: NidusEntry) => {
        ensureNotNullOrUndefined(updateEntryFormResultDeferred.current);
        setShowUpdateEntryForm(false);
        setEntryForUpdate(undefined);
        updateEntryFormResultDeferred.current.resolve(updatedItem);
    };

    const handleUpdateEntryFormError = () => {
        ensureNotNullOrUndefined(updateEntryFormResultDeferred.current);
        // Показать ошибку
        setShowUpdateEntryForm(false);
        setEntryForUpdate(undefined);
        updateEntryFormResultDeferred.current.resolve(undefined);
    };

    const contextualActions = React.useMemo(
        () => ({
            ["userPicklist.AddToReference"]: async () => {
                await handleCreateNewFromSelect();
            },
        }),
        []
    );

    const userPicklist = React.useRef<IUserPicklist>({
        getExistingValue: () => {
            const result = new Deferred<undefined | NidusEntry>();
            selectValueDeferred.current = result;
            setGetValueFromReferenceModal(true);
            return result.promise;
        },
        createNewValue: createEntry,
    });

    React.useEffect(() => {
        const unregisterCallback = userPicklistRegistry.registerUserPicklist(props.name, userPicklist.current);
        return unregisterCallback;
    }, []);

    return (
        <ContextualActionsProvider actions={contextualActions}>
            <div>
                {getValueFromReferenceModal && (
                    <SelectNidusEntryModal
                        nidusClient={nidusClient}
                        onSelect={handleSelectEntryFromModal}
                        onClose={handleSelectEntryModalClose}
                        columns={props.columns}
                        title={props.pickerTitle}
                        addButtonText={props.pickerCreateLink}
                        emptyMessage={props.emptyPickerMessage}
                        onRequestAddEntry={handleCreateNewFromSelect}
                        onRequestUpdateEntry={handleRequestUpdateEntry}
                        onRequestDeleteEntry={handleDeleteFromSelect}
                        onRequestRestoreItem={handleRequestRestoreItem}
                    />
                )}
                {showCreateEntryForm && (
                    <CreateNidusEntryFormModal
                        nidusClient={nidusClient}
                        onClose={handleCreateEntryFormClose}
                        onItemCreated={handleCreateEntryFormSave}
                        editor={props.editor}
                        editorValidations={props.editorValidations}
                        kcXmlValidationConfigurator={props.kcXmlValidationConfigurator}
                        editorDataDeclaration={props.editorDataDeclaration}
                        title={props.createTitle}
                        draftId={props.draftId}
                        keformsUrl={props.keformsUrl}
                        onError={handleCreateEntryFormError}
                    />
                )}
                {showUpdateEntryForm && (
                    <UpdateNidusEntryFormModal
                        entryForUpdate={entryForUpdate ?? reject()}
                        nidusClient={nidusClient}
                        onClose={handleUpdateEntryFormClose}
                        onItemUpdated={handleUpdateEntryFormSave}
                        editor={props.editor}
                        editorValidations={props.editorValidations}
                        kcXmlValidationConfigurator={props.kcXmlValidationConfigurator}
                        editorDataDeclaration={props.editorDataDeclaration}
                        title={props.editTitle}
                        draftId={props.draftId}
                        keformsUrl={props.keformsUrl}
                        onError={handleUpdateEntryFormError}
                    />
                )}
            </div>
        </ContextualActionsProvider>
    );
}
